/**
 * Responsive Dashboard — content-only component.
 *
 * After the AeroLayout refactor, auth, subscription fetching, sidebar, and
 * GlassProvider all live in AeroLayout. This component is a pure content
 * panel: it receives subscription info via outlet context and only fetches
 * Responsive-specific usage data from Firestore.
 */
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import firebase from '../../firebase';
import { handlePaymentClick } from '../../utlils/paymentUtil';
import { usePlans } from '../../hooks/usePlans';
import { usePricing } from '../../hooks/usePricing';
import GlassButton from '../../components/Aero/GlassButton';
import GlassTag from '../../components/Aero/GlassTag';
import { Helmet } from 'react-helmet-async';
import './ResponsiveDashboard.css';

const GENERATION_LIMITS = {
  anonymous: 5,
  free: 10,
  basic: 25,
  solo: 60,
  pro: 200,
};

function ResponsiveDashboard() {
  // Subscription data comes from AeroLayout — fetched once, never refetched on nav
  const { user, planKey, subscriptionStatus } = useOutletContext();
  const dbFirestore = firebase.firestore();
  const { plans } = usePlans();
  const { pricing } = usePricing();

  // Responsive-specific: monthly generation usage
  const [usageCount, setUsageCount] = useState(0);
  const [usageLoading, setUsageLoading] = useState(true);
  const [error, setError] = useState(null);

  // currentPlan from AeroLayout context
  const currentPlan = planKey; // 'free' | 'basic' | 'solo' | 'pro'

  useEffect(() => {
    if (!user?.uid) return;

    const fetchUsage = async () => {
      try {
        const usageDoc = await dbFirestore
          .collection('responsiveUsage')
          .doc(user.uid)
          .get();

        if (usageDoc.exists) {
          const data = usageDoc.data();
          const now = new Date();
          const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          setUsageCount(data.month === currentMonth ? (data.count || 0) : 0);
        } else {
          setUsageCount(0);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching responsive usage:', err);
        setError('Failed to load your usage data. Please try refreshing.');
      } finally {
        setUsageLoading(false);
      }
    };

    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const generationLimit = GENERATION_LIMITS[currentPlan] || GENERATION_LIMITS.free;
  const usagePercent = Math.min((usageCount / generationLimit) * 100, 100);

  const handleUpgrade = async (priceId) => {
    if (!user) return;
    const subsSnapshot = await dbFirestore
      .collection('user')
      .doc(user.uid)
      .collection('subscriptions')
      .limit(1)
      .get();
    const trialConsume = subsSnapshot.empty ? 'false' : 'true';
    await handlePaymentClick(user, priceId, trialConsume);
  };

  const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

  const upgradePlans = [
    { key: 'basic', generations: 25, label: 'Basic' },
    { key: 'solo', generations: 60, label: 'Solo' },
    { key: 'pro', generations: 200, label: 'Pro' },
  ];

  // NOTE: No early return on usageLoading — the header and page chrome render
  // immediately every time. Only the usage data area shows an inline skeleton
  // while the Firestore fetch resolves. This prevents the flash where the entire
  // content area was replaced by two grey boxes on every navigation to /responsive.

  if (error) {
    return (
      <div className="ut-dashboard responsive-dash-error-wrap">
        <p className="responsive-dash-error-msg">{error}</p>
        <GlassButton fill="grey" onClick={() => window.location.reload()}>
          Retry
        </GlassButton>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Responsive | Aero</title>
        <meta name="description" content="AI-powered responsive design generation for your Figma frames" />
      </Helmet>

      <div className="ut-dashboard">
        {/* Header — always visible immediately, never replaced by a skeleton */}
        <header className="ut-dashboard-header">
          <div className="ut-dashboard-header-left">
            <h1 className="ut-dashboard-title">Responsive</h1>
            {subscriptionStatus && (
              <GlassTag color={subscriptionStatus === 'active' ? undefined : subscriptionStatus === 'trialing' ? '#3b82f6' : '#ef4444'}>
                {capitalize(subscriptionStatus)}
              </GlassTag>
            )}
          </div>
        </header>

        {/* Plan / Usage Card — shows inline skeleton only for the data portion */}
        <div className="ut-card responsive-dash-plan-card">
          {usageLoading ? (
            <div className="responsive-dash-usage-loading">
              <div className="ut-skeleton" style={{ width: '40%', height: 14, marginBottom: 12 }} />
              <div className="ut-skeleton responsive-dash-usage-bar" style={{ marginBottom: 8 }} />
              <div className="ut-skeleton" style={{ width: '30%', height: 12 }} />
            </div>
          ) : (
            <>
              <div className="responsive-dash-plan-header">
                <h2 className="responsive-dash-plan-name">{capitalize(currentPlan)} Plan</h2>
              </div>

              <div className="responsive-dash-usage-section">
                <div className="responsive-dash-usage-label">
                  <span>Generations this month</span>
                  <span className="responsive-dash-usage-count">{usageCount} / {generationLimit}</span>
                </div>
                <div className="ut-sidebar-usage-bar responsive-dash-usage-bar">
                  <div
                    className={`ut-sidebar-usage-fill ${usagePercent >= 90 ? 'full' : usagePercent >= 70 ? 'warning' : ''}`}
                    style={{ width: `${usagePercent}%` }}
                  />
                </div>
                {usageCount >= generationLimit && (
                  <p className="responsive-dash-usage-exceeded">
                    You've reached your generation limit. Upgrade for more.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Upgrade Section */}
        {!usageLoading && currentPlan !== 'pro' && (
          <div className="responsive-dash-upgrade-section">
            <h2 className="responsive-dash-section-title">Upgrade your plan</h2>
            <p className="responsive-dash-section-subtitle">Get more generations per month</p>
            <div className="responsive-dash-plans-grid">
              {upgradePlans
                .filter((p) => {
                  const order = { free: 0, basic: 1, solo: 2, pro: 3 };
                  return order[p.key] > order[currentPlan];
                })
                .map((plan) => {
                  const planConfig = plans?.[plan.key];
                  const planPricing = pricing?.[plan.key];
                  const monthlyPriceId = planPricing?.monthly?.priceId;
                  const monthlyDisplay = planPricing?.monthly?.displayAmount;
                  return (
                    <div key={plan.key} className="ut-card responsive-dash-plan-option">
                      <div className="responsive-dash-plan-option-header">
                        <h3>{plan.label}</h3>
                        {planConfig?.description && (
                          <p className="responsive-dash-plan-desc">{planConfig.description}</p>
                        )}
                      </div>
                      <div className="responsive-dash-plan-option-body">
                        <p className="responsive-dash-plan-generations">
                          <span className="responsive-dash-plan-gen-number">{plan.generations}</span>
                          <span> generations / month</span>
                        </p>
                        {monthlyDisplay && (
                          <p className="responsive-dash-plan-price">{monthlyDisplay}/mo</p>
                        )}
                      </div>
                      <GlassButton
                        fill="aero-lt"
                        style={{ width: '100%' }}
                        onClick={() => monthlyPriceId && handleUpgrade(monthlyPriceId)}
                        disabled={!monthlyPriceId}
                      >
                        Upgrade to {plan.label}
                      </GlassButton>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Quick Start */}
        <div className="responsive-dash-quickstart">
          <h2 className="responsive-dash-section-title">Quick start</h2>
          <div className="responsive-dash-steps">
            <div className="ut-card responsive-dash-step">
              <div className="responsive-dash-step-number">1</div>
              <div>
                <h3>Open the plugin</h3>
                <p>Open the <strong>Responsive</strong> plugin in Figma</p>
              </div>
            </div>
            <div className="ut-card responsive-dash-step">
              <div className="responsive-dash-step-number">2</div>
              <div>
                <h3>Select a frame</h3>
                <p>Select a desktop frame in your Figma file</p>
              </div>
            </div>
            <div className="ut-card responsive-dash-step">
              <div className="responsive-dash-step-number">3</div>
              <div>
                <h3>Generate</h3>
                <p>Click Generate — your mobile layout is created automatically</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ResponsiveDashboard;
